import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconFileExport } from "@/lib/icons";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { downloadCardPdf } from "@/lib/api/patients";

const LANGS = ["uz", "ru", "en"] as const;

export function GenerateCardDialog({
  patientId,
  cardNumber,
  opened,
  onClose,
}: {
  patientId: string;
  cardNumber?: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const form = useForm({
    initialValues: { lang: i18n.language as string },
  });

  const mutation = useMutation({
    mutationFn: async (lang: string) => {
      const blob = await downloadCardPdf(patientId, lang);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return { url };
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        title: t("patients.card_generated"),
        message: cardNumber ?? "",
      });
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal opened={opened} onClose={onClose} title={t("patients.generate_card_title")} size="md">
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v.lang))}>
        <Stack>
          <Text size="sm" c="dimmed">
            {t("patients.generate_card_hint")}
          </Text>
          <Select
            label={t("patients.fields.language")}
            data={LANGS.map((l) => ({
              value: l,
              label: t(`languages.${l}`, { defaultValue: l.toUpperCase() }),
            }))}
            {...form.getInputProps("lang")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              leftSection={<IconFileExport size={16} />}
              loading={mutation.isPending}
              radius="xl"
            >
              {t("patients.generate_card")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
